import React from 'react';
import styled from 'styled-components';
import { Image, Type } from '@spotify-internal/encore-creator-web';
import { spacer16, spacer24, spacer48, screenMdMin } from '@spotify-internal/encore-foundation';
import { AzureLink } from './styles';
import { pageConstraintStyles, PAGE_GUTTER } from './styles';

export function Testimonials() {
  return (
    <TestimonialsContainer>
      <Testimonial>
        <TestimonialHeader>
          <TestimonialCover
            src="/images/landing_page/testimonial-01-cover.png"
            alt="Revisionist History cover"
          />
          <TestimonialAvatar
            src="/images/landing_page/testimonial-01-profile.png"
            alt="Heather Fain"
          />
        </TestimonialHeader>
        <TestimonialTitle>Revisionist History</TestimonialTitle>
        <TestimonialContent>
          “Spotify for Podcasters provides us with an easy way to track audience growth for
          established shows, like Malcolm Gladwell’s Revisionist History, and for those that are
          just starting, like Michael Lewis’s Against the Rules. The feedback we gather about how
          our episodes are engaging listeners&nbsp;— and who those listeners are&nbsp;— helps us
          build effective marketing strategies.”
        </TestimonialContent>
        <Type as="p" variant={Type.body2}>
          <Type as="span" weight={Type.bold}>
            Heather Fain
          </Type>
          <br /> CMO, Pushkin Industries
          <br />
          <AzureLink
            href="https://open.spotify.com/show/2LOJaYKijiwNefCvzczyib?si=jqZkktjOQu-v-4J9Ba41jg"
            target="_blank"
            rel="noopener noreferrer"
          >
            Revisionist History
          </AzureLink>
        </Type>
        <TestimonialsDecorationSawtooth />
      </Testimonial>
      <Testimonial>
        <TestimonialHeader>
          <TestimonialCover
            src="/images/landing_page/testimonial-02-cover.png"
            alt="Therapy for Black Girls"
          />
          <TestimonialAvatar
            src="/images/landing_page/testimonial-02-profile.png"
            alt="Dr. Joy Harden Bradford"
          />
        </TestimonialHeader>
        <TestimonialTitle>Therapy for Black Girls</TestimonialTitle>
        <TestimonialContent>
          “I love having Spotify as a home for my podcast. I use the analytics provided to see which
          topics my listeners enjoy most and then craft additional content around these topics to
          dig deeper and further engage my audience. Listeners have also commented that they enjoy
          being able to find the podcast on a platform they already use which is a win for everyone.
          I appreciate that Spotify takes special care to be sure that the people who need and want
          the information on my podcast can access it.”
        </TestimonialContent>
        <Type as="p" variant={Type.body2}>
          <Type as="span" weight={Type.bold}>
            Dr. Joy Harden Bradford
          </Type>
          <br /> Host
          <br />
          <AzureLink
            href="https://open.spotify.com/show/7kPXrtK66kXyiqpKTtsqNN"
            target="_blank"
            rel="noopener noreferrer"
          >
            Therapy for Black Girls
          </AzureLink>
        </Type>
        <TestimonialsDecorationConfetti />
      </Testimonial>
      <Testimonial>
        <TestimonialHeader>
          <TestimonialCover
            src="/images/landing_page/testimonial-03-cover.png"
            alt="Beach Too Sandy, Water Too Wet cover"
          />
          <TestimonialAvatar
            src="/images/landing_page/testimonial-03-profile.png"
            alt="Alex and Christine Schiefer"
          />
        </TestimonialHeader>
        <TestimonialTitle>Beach Too Sandy, Water Too Wet</TestimonialTitle>
        <TestimonialContent>
          “Support from Spotify has given us the opportunity to reach listeners from all over the
          world. Not even a month into our show, we found ourselves featured on the podcast
          homepage, leading to massive growth in our audience. Spotify’s platform continues to
          unlock our show’s full potential.”
        </TestimonialContent>
        <Type as="p" variant={Type.body2}>
          <Type as="span" weight={Type.bold}>
            Alex and Christine Schiefer
          </Type>
          <br /> Hosts
          <br />
          <AzureLink
            href="https://open.spotify.com/show/0UfNJ2DoIfIveU2hghm1Io"
            target="_blank"
            rel="noopener noreferrer"
          >
            Beach Too Sandy, Water Too Wet
          </AzureLink>
        </Type>
      </Testimonial>
    </TestimonialsContainer>
  );
}

const TestimonialsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: ${spacer48};
  margin: 50px ${spacer16};

  @media (min-width: ${screenMdMin}) {
    ${pageConstraintStyles}

    grid-template-columns: repeat(3, 1fr);
    grid-column-gap: 68px;
    padding: 238px ${PAGE_GUTTER} 128px;
  }
`;

const TestimonialCover = styled.img`
  width: 275px;

  @media (min-width: ${screenMdMin}) {
    width: 150px;
  }
`;

const TestimonialAvatar = styled(Image).attrs({
  crop: true,
  circle: true,
})`
  bottom: 0;
  position: absolute;
  transform: translateX(-50%);
  width: 136px;
  height: 136px;

  @media (min-width: ${screenMdMin}) {
    width: 75px;
    height: 75px;
  }
`;

const TestimonialHeader = styled.div`
  position: relative;
`;

const Testimonial = styled.div`
  z-index: 1;
`;

const TestimonialTitle = styled(Type).attrs({
  forwardedAs: 'h3',
  variant: Type.heading3,
})`
  font-size: 27px;
  padding: ${spacer24} 0;
`;

const TestimonialContent = styled(Type).attrs({
  forwardedAs: 'p',
  variant: Type.body1,
})`
  line-height: 26px;
`;

const TestimonialsDecorationSawtooth = styled.div`
  display: none;

  @media (min-width: ${screenMdMin}) {
    background-image: url('/images/landing_page/horizontal-sawtooth.png');
    background-size: 156px 40px;
    display: block;
    width: 156px;
    height: 40px;
    transform: translate(-68px, 40px);
  }

  @media (min-width: ${screenMdMin}) and (min-resolution: 192dpi) {
    background-image: url('/images/landing_page/horizontal-sawtooth@2x.png');
  }
`;

const TestimonialsDecorationConfetti = styled.div`
  display: none;

  @media (min-width: ${screenMdMin}) {
    display: block;
    position: absolute;
    background-image: url('/images/landing_page/testimonial-confetti.png');
    background-size: 319px 240px;
    width: 319px;
    height: 240px;
    transform: translate(150px, -140px);
  }

  @media (min-width: ${screenMdMin}) and (min-resolution: 192dpi) {
    background-image: url('/images/landing_page/testimonial-confetti@2x.png');
  }
`;
